import { FC, Props } from "./typings";
import styles from "./styles.module.scss";
import { useState } from "react";
import { ETypes } from "components/Settings/IntegrationMenu/typings";
import { IntegrationProvider } from "dto/Settings/IntegrationProvider";
import ImapIcon from "assets/icons/filters/imap.svg?react";
import OutlookIcon from "assets/icons/filters/outlook.svg?react";
import GmailIcon from "assets/icons/filters/Gmail.svg?react";
import ConfigureIMAP from "components/ConfigureIMAP";
import OauthPopup from "components/OauthPopup";
import { CodeResponse, useGoogleLogin } from "@react-oauth/google";
import { IntegrationType } from "dto/Settings/IntegrationType";
import settingService from "api/settingsService";
import { ENotificationType, notification } from "utils/notification";
import { getErrorMessage } from "utils/getErrorMessage";
import CloseIcon from "assets/icons/close.svg?react";
import { useTranslation } from "react-i18next";

export interface IApplication {
  type: ETypes;
  image: any;
  name: string;
  description: string;
  provider?: IntegrationProvider;
}

export const APPLICATIONS: IApplication[] = [
  {
    type: ETypes.EMAIL,
    image: <OutlookIcon />,
    name: "Outlook",
    description: "integrations.outlookDescription",
    provider: IntegrationProvider.Microsoft,
  },
  {
    type: ETypes.EMAIL,
    image: <GmailIcon />,
    name: "Gmail",
    description: "integrations.gmailDescription",
    provider: IntegrationProvider.Google,
  },
  {
    type: ETypes.EMAIL,
    image: <ImapIcon />,
    name: "IMAP/POP3",
    description: "integrations.imapDescription",
    provider: IntegrationProvider.Imap,
  },
];

const EmailConfiguration: FC<Props> = ({ toggle }) => {
  const { t } = useTranslation();
  const list = APPLICATIONS;
  const [confImap, setConfImap] = useState(false);

  const { mutate: configureIntegration } =
    settingService.useConfigureIntegration();

  const scopesEmail = [
    "https://mail.google.com/",
    "https://www.googleapis.com/auth/contacts.readonly",
  ] as string[];

  const { mutate: getMsRedirect } = settingService.useGetMsredirect();

  const [msRedirecUrl, setMsRedirecUrl] = useState("");

  const onClick = (application: IApplication) => {
    if (application.type === ETypes.EMAIL) {
      if (application.provider === IntegrationProvider.Google) {
        loginGoogleEmail();
        return;
      }
      if (application.provider === IntegrationProvider.Microsoft) {
        initMicrosoft();
        return;
      }
      if (application.provider === IntegrationProvider.Imap) {
        setConfImap(true);
        return;
      }
    }
  };

  const loginGoogleEmail = useGoogleLogin({
    scope: scopesEmail?.join(" "),
    flow: "auth-code",
    onSuccess: (codeResponse) => {
      handleGoogleSuccess(codeResponse, scopesEmail);
    },
  });

  const initMicrosoft = () => {
    getMsRedirect(
      {
        scopes: [
          "user.read",
          "mail.readwrite",
          "mail.send",
          "contacts.readwrite",
        ].join(", "),
      },
      {
        onSuccess(data) {
          const url = data.data.Url;
          setMsRedirecUrl(url);
        },
      },
    );
  };

  const onCode = (code: any) => {
    configureIntegration(
      {
        Provider: IntegrationProvider.Microsoft,
        Type: IntegrationType.Email,
        Code: code,
        Scope: [
          "user.read",
          "mail.readwrite",
          "mail.send",
          "contacts.readwrite",
        ].join(", "),
      },
      {
        onSuccess() {
          toggle();
        },
        onError(e: any) {
          notification({
            title: t("integrations.configureFailed"),
            text: getErrorMessage(e.response.data),
            type: ENotificationType.ERROR,
          });
          setMsRedirecUrl(null);
        },
      },
    );
  };

  const handleGoogleSuccess = (
    codeResponse: Omit<
      CodeResponse,
      "error" | "error_description" | "error_uri"
    >,
    scopes,
  ) => {
    if (codeResponse) {
      let missing = false;
      scopes?.forEach((s) => {
        if (codeResponse.scope.indexOf(s) < 0) {
          missing = true;
        }
      });

      if (missing) {
        notification({
          title: t("integrations.insufficientPermissions"),
          text: t("integrations.integrationSetupFailed"),
          type: ENotificationType.ERROR,
        });
        return;
      }

      configureIntegration(
        {
          Provider: IntegrationProvider.Google,
          Type: IntegrationType.Email,
          Code: codeResponse.code,
          Scope: codeResponse.scope,
        },
        {
          onSuccess() {
            toggle();
          },
          onError(e: any) {
            notification({
              title: t("integrations.configureFailed"),
              text: getErrorMessage(e.response.data),
              type: ENotificationType.ERROR,
            });
          },
        },
      );
    }
  };

  const onClose = () => {};

  return (
    <div className={styles.settingsWrapper}>
      <OauthPopup
        width={400}
        height={600}
        title={t("common.signIn")}
        url={msRedirecUrl}
        onClose={onClose}
        onCode={onCode}
      >
        <div></div>
      </OauthPopup>

      <div className={styles.settings}>
        <div className={styles.title}>
          {t("integrations.emailConfigTitle")}
          <CloseIcon onClick={() => toggle()} className={styles.closeIcon} />
        </div>

        {confImap && (
          <ConfigureIMAP
            onSuccess={() => {
              toggle();
              // refetchI();
              // toggle();
            }}
            toggle={() => setConfImap(false)}
          />
        )}

        {!confImap && (
          <>
            <div className={styles.sub}>
              {t("integrations.emailConfigSubtitle")}
            </div>
            <div className={styles.applications}>
              {list.map((a, index) => (
                <div
                  onClick={() => onClick(a)}
                  className={styles.application}
                  key={"appInteg" + index}
                >
                  <div className={styles.appIcon}>{a.image}</div>
                  <div className={styles.appTitle}>{a.name}</div>
                  <div className={styles.appDescription}>{t(a.description)}</div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default EmailConfiguration;
